import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEnvelope, FaLock, FaPhone, FaKey } from "react-icons/fa";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { base_url } from "../utils/axiosConfig";
import { useDispatch } from "react-redux";
import { registerUser } from "../features/user/userSlice";
import { motion, AnimatePresence } from "framer-motion";
import logo from '../images/HeadLogo.png';

// Debounce function to prevent rapid API calls
const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

// Check Email Exists API
const checkEmailExists = async (email) => {
  try {
    const res = await axios.get(`${base_url}user/all-users`);
    return res.data.some((u) => u.email.toLowerCase() === email.toLowerCase());
  } catch (error) {
    console.error("Fetch error:", error);
    return false;
  }
};

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [emailExists, setEmailExists] = useState(false);
  const [timer, setTimer] = useState(600); // 10 minutes

  const timerRef = useRef(null);

  // Start OTP Timer
  const startOtpTimer = () => {
    setTimer(600);
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Debounced API call for checking email
  const debouncedEmailCheck = useRef(
    debounce(async (value) => {
      const exists = await checkEmailExists(value);
      setEmailExists(exists);
    }, 600)
  ).current;

  // Formik Setup
  const formik = useFormik({
    initialValues: {
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      email: yup.string().email("Invalid email").required("Email is required*"),
      phoneNumber: yup.string().required("Phone Number required*"),
      password: yup.string().required("Password required*"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords do not match")
        .required("Confirm Password required*"),
    }),

    onSubmit: async (values) => {
      try {
        if (emailExists) {
          toast.error("Email already registered!");
          return;
        }

        setLoading(true);

        const result = await dispatch(registerUser(values)).unwrap();

        if (result?.userId) {
          setUserId(result.userId);
          setStep(2);
          toast.success("OTP sent successfully!");
          startOtpTimer(); // Start countdown
        }
      } catch (error) {
        toast.error(error || "Registration failed");
      } finally {
        setLoading(false);
      }
    },
  });

  // Trigger email validation API ONLY when typing email
  useEffect(() => {
    if (formik.values.email) {
      debouncedEmailCheck(formik.values.email);
    }
  }, [formik.values.email]);

  // OTP input handler
  const handleOtpChange = (val, idx) => {
    if (/^\d?$/.test(val)) {
      const newArr = [...otp];
      newArr[idx] = val;
      setOtp(newArr);

      if (val && idx < 5) {
        document.getElementById(`otp-${idx + 1}`).focus();
      }
    }
  };

  // OTP Submit
  const handleOtpSubmit = async () => {
    if (timer === 0) {
      toast.error("OTP expired! Please request again.");
      return;
    }

    const code = otp.join("");

    if (code.length !== 6) {
      toast.error("Enter a valid 6-digit OTP");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${base_url}user/verify-otp`, {
        userId,
        otp: code,
      });

      if (res.status === 200) {
        toast.success("OTP Verified! Signup Complete. Please Login to continue.");
        navigate("/login");
      } else {
        toast.error("Invalid OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "OTP Verification Failed");
    } finally {
      setLoading(false);
    }
  };

  // Format timer mm:ss
  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div 
      className="min-vh-100 d-flex justify-content-center align-items-center p-3"
      style={{
        background: 'linear-gradient(135deg, #f0f9f0 0%, #e8f5e9 100%)',
        fontFamily: "'Poppins', sans-serif"
      }}
    >
      <Toaster position="top-center" />

      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-100"
        style={{ maxWidth: '400px' }}
      >
        <div 
          className="card border-0 shadow-lg"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 15px 50px rgba(45, 90, 39, 0.1)'
          }}
        >
          <div className="card-body p-4">
            {/* Simple Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="btn border-0 d-flex align-items-center gap-2 text-secondary mb-3 p-0 auth-back-btn"
              style={{ fontSize: '13px', transition: '0.2s' }}
            >
              <FaArrowLeft size={12} />
              <span>Back</span>
            </button>

            {/* HEADER */}
            <div className="text-center mb-3">
              <motion.img 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                src={logo} 
                alt="Logo" 
                className="mb-2" 
                style={{ height: '48px', objectFit: 'contain' }} 
              />
              <h4 className="fw-bold mb-1" style={{ color: '#1b5e20', letterSpacing: '-0.3px' }}>
                {step === 1 ? "Create Account" : "Verify OTP"}
              </h4>
              <p className="text-muted small mb-0" style={{ fontSize: '13px' }}>
                {step === 1
                  ? "Join our grocery family & start shopping!"
                  : "Enter the OTP sent to your email"}
              </p>
            </div>

            {/* STEP 1 */}
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.form 
                  key="step1"
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -15 }}
                  onSubmit={formik.handleSubmit} 
                  className="d-flex flex-column gap-2"
                >
                  {/* EMAIL */}
                  <div className="form-group">
                    <label className="form-label small fw-bold text-dark mb-1 ms-1" style={{ fontSize: '12px' }}>Email Address</label>
                    <div className="input-group premium-input-group">
                      <span className="input-group-text bg-white border-end-0 text-secondary ps-3" style={{ padding: '8px' }}>
                        <FaEnvelope size={14} />
                      </span>
                      <input
                        className="form-control border-start-0 ps-2 py-2"
                        style={{ fontSize: '14px', borderRadius: '0 10px 10px 0', height: '42px' }}
                        type="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    {emailExists && <small className="text-danger ms-1 fw-medium" style={{ fontSize: '11px' }}>Email already exists!</small>}
                    {formik.touched.email && formik.errors.email && (
                      <small className="text-danger ms-1 fw-medium" style={{ fontSize: '11px' }}>{formik.errors.email}</small>
                    )}
                  </div>

                  {/* PHONE */}
                  <div className="form-group">
                    <label className="form-label small fw-bold text-dark mb-1 ms-1" style={{ fontSize: '12px' }}>Phone Number</label>
                    <div className="input-group premium-input-group">
                      <span className="input-group-text bg-white border-end-0 text-secondary ps-3" style={{ padding: '8px' }}>
                        <FaPhone size={14} />
                      </span>
                      <input
                        className="form-control border-start-0 ps-2 py-2"
                        style={{ fontSize: '14px', borderRadius: '0 10px 10px 0', height: '42px' }}
                        type="number"
                        name="phoneNumber"
                        placeholder="Enter phone number"
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                      <small className="text-danger ms-1 fw-medium" style={{ fontSize: '11px' }}>{formik.errors.phoneNumber}</small>
                    )}
                  </div>

                  {/* PASSWORD */}
                  <div className="form-group">
                    <label className="form-label small fw-bold text-dark mb-1 ms-1" style={{ fontSize: '12px' }}>Password</label>
                    <div className="input-group premium-input-group">
                      <span className="input-group-text bg-white border-end-0 text-secondary ps-3" style={{ padding: '8px' }}>
                        <FaLock size={14} />
                      </span>
                      <input
                        className="form-control border-start-0 ps-2 py-2"
                        style={{ fontSize: '14px', borderRadius: '0 10px 10px 0', height: '42px' }}
                        type="password"
                        name="password"
                        placeholder="Create password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <small className="text-danger ms-1 fw-medium" style={{ fontSize: '11px' }}>{formik.errors.password}</small>
                    )}
                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className="form-group">
                    <label className="form-label small fw-bold text-dark mb-1 ms-1" style={{ fontSize: '12px' }}>Confirm Password</label>
                    <div className="input-group premium-input-group">
                      <span className="input-group-text bg-white border-end-0 text-secondary ps-3" style={{ padding: '8px' }}>
                        <FaLock size={14} />
                      </span>
                      <input
                        className="form-control border-start-0 ps-2 py-2"
                        style={{ fontSize: '14px', borderRadius: '0 10px 10px 0', height: '42px' }}
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                      <small className="text-danger ms-1 fw-medium" style={{ fontSize: '11px' }}>{formik.errors.confirmPassword}</small>
                    )}
                  </div>

                  {/* BUTTON */}
                  <button
                    className="btn btn-success w-100 py-2 mt-2 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                    style={{
                      background: "#3b7d2f",
                      borderColor: "#3b7d2f",
                      borderRadius: "12px",
                      fontSize: "15px",
                      height: "45px",
                      transition: "all 0.3s ease"
                    }}
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>Sending...
                      </>
                    ) : (
                      "Send OTP"
                    )}
                  </button>

                  <p className="text-center mt-2 small" style={{ fontSize: '13px' }}>
                    Already have an account?{" "}
                    <Link to="/login" className="fw-bold text-decoration-none" style={{ color: "#2e7d32" }}>
                      Login
                    </Link>
                  </p>
                </motion.form>
              )}

              {/* STEP 2 - OTP */}
              {step === 2 && (
                <motion.div 
                  key="step2"
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-center"
                >
                  {/* TIMER */}
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-3 p-2 rounded-3 bg-light" style={{ fontSize: '13px' }}>
                    <span className="text-muted fw-medium">Time Remaining:</span>
                    <span className="fw-bold" style={{ color: "#d32f2f" }}>{formatTime(timer)}</span>
                  </div>

                  <div className="d-flex justify-content-center gap-2 mt-2 mb-4">
                    {otp.map((val, i) => (
                      <input
                        key={i}
                        id={`otp-${i}`}
                        type="text"
                        maxLength={1}
                        disabled={timer === 0}
                        className="form-control text-center p-0"
                        style={{
                          width: "42px",
                          height: "52px",
                          fontSize: "1.3rem",
                          fontWeight: "700",
                          borderRadius: "10px",
                          border: "2px solid #e0e0e0",
                          backgroundColor: "#fcfdfc",
                          transition: "all 0.2s ease"
                        }}
                        value={val}
                        onChange={(e) => handleOtpChange(e.target.value, i)}
                        onFocus={(e) => e.target.style.borderColor = "#3b7d2f"}
                        onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                      />
                    ))}
                  </div>

                  <button
                    className="btn btn-success w-100 py-2 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 mb-2"
                    style={{
                      background: "#3b7d2f",
                      borderColor: "#3b7d2f",
                      borderRadius: "12px",
                      height: "45px",
                    }}
                    onClick={handleOtpSubmit}
                    disabled={loading || timer === 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>Verifying...
                      </>
                    ) : (
                      "Verify OTP"
                    )}
                  </button>
                  
                  <button 
                    className="btn btn-link text-decoration-none text-success small fw-bold"
                    style={{ fontSize: '13px' }}
                    onClick={() => setStep(1)}
                  >
                    Back to Signup
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      <style>{`
        .premium-input-group .form-control {
          border-color: #e0e0e0;
          background-color: #fcfdfc;
          transition: all 0.2s ease;
        }
        .premium-input-group .input-group-text {
          border-color: #e0e0e0;
          background-color: #fcfdfc;
        }
        .premium-input-group:focus-within .form-control,
        .premium-input-group:focus-within .input-group-text {
          border-color: #3b7d2f !important;
          background-color: #fff !important;
          box-shadow: none;
        }
        .premium-input-group:focus-within {
          box-shadow: 0 0 0 3px rgba(59, 125, 47, 0.1);
          border-radius: 10px;
        }
        .form-control:focus {
          box-shadow: none !important;
        }
        .btn-success:hover {
          background-color: #2d5a27 !important;
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(59, 125, 47, 0.25) !important;
        }
        .btn-success:active {
          transform: translateY(0);
        }
        .auth-back-btn:hover {
          color: #1b5e20 !important;
          transform: translateX(-2px);
        }
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        @media (max-width: 576px) {
          .card-body {
            padding: 24px 20px !important;
          }
        }
      `}</style>
    </div>
  );

};

export default Signup;

